import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useAsync, useCart, useStore } from '../lib/store'
import { ErrorNote, Fill, Loading, ProductCard, Shell, Stars } from '../components/Layout'

function ReviewForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  const [author, setAuthor] = useState('')
  const [location, setLocation] = useState('')
  const [rating, setRating] = useState(5)
  const [body, setBody] = useState('')
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr('')
    setMsg('')
    try {
      const r = await api.submitReview({ productSlug: slug, author, location, rating, body })
      setMsg(r.message)
      setAuthor('')
      setLocation('')
      setBody('')
      onDone()
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not send your review.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520 }}>
      <h3 style={{ margin: 0, fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--muted)' }}>Write a review</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
        <div className="field">
          <label htmlFor="rv-name">Your name</label>
          <input id="rv-name" value={author} onChange={(e) => setAuthor(e.target.value)} required minLength={2} maxLength={60} />
        </div>
        <div className="field">
          <label htmlFor="rv-loc">Town or city</label>
          <input id="rv-loc" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={60} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="rv-rating">Rating</label>
        <select id="rv-rating" value={rating} onChange={(e) => setRating(Number(e.target.value))}>
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>
              {n} star{n === 1 ? '' : 's'}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="rv-body">Your review</label>
        <textarea id="rv-body" value={body} onChange={(e) => setBody(e.target.value)} required minLength={10} maxLength={2000} />
      </div>
      {err && <div className="notice notice-error">{err}</div>}
      {msg && <div className="notice notice-ok">{msg}</div>}
      <button className="btn btn-sm" disabled={busy} style={{ alignSelf: 'flex-start' }}>
        {busy ? 'Sending…' : 'Submit review'}
      </button>
    </form>
  )
}

export default function Product() {
  const { slug = '' } = useParams()
  const { add } = useCart()
  const { money } = useStore()
  const { data, loading, error, reload } = useAsync(() => api.product(slug), [slug])
  const [qty, setQty] = useState(1)
  const [tab, setTab] = useState<'description' | 'ingredients' | 'how'>('description')

  if (loading) return <Shell><Loading label="Loading product" /></Shell>
  if (error || !data) return <Shell><ErrorNote message={error ?? 'Product not found.'} onRetry={reload} /></Shell>

  const p = data

  return (
    <Shell>
      <nav className="section-tight" style={{ paddingBottom: 0, fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--muted)' }}>
        <Link to="/shop">Shop</Link> <span aria-hidden="true">/</span> {p.category} <span aria-hidden="true">/</span> {p.name}
      </nav>

      <section className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 'clamp(20px,3vw,48px)', padding: 'clamp(24px,3vw,44px) clamp(16px,3vw,44px)' }}>
        <div style={{ position: 'relative', aspectRatio: '4 / 5', background: 'var(--stone)', overflow: 'hidden' }}>
          <Fill src={p.image} alt={p.name} label={p.name} />
          {p.badge && p.inStock && <span className="badge">{p.badge}</span>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'center' }}>
          {p.step && <div className="eyebrow">{p.step}</div>}
          <h1 className="display" style={{ fontSize: 'clamp(26px,3vw,42px)' }}>{p.name}</h1>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 300, color: 'var(--muted)' }}>{p.tagline}</p>

          {p.reviewCount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Stars rating={p.rating} count={p.reviewCount} />
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span style={{ fontFamily: 'var(--serif)', fontSize: 30, fontWeight: 300 }}>{money(p.price)}</span>
            {p.comparePrice && <span style={{ fontSize: 13, color: 'var(--muted-2)', textDecoration: 'line-through' }}>{money(p.comparePrice)}</span>}
            {p.size && <span className="small">· {p.size}</span>}
          </div>

          {!p.inStock ? (
            <div className="notice notice-error" style={{ maxWidth: 360 }}>This product is currently sold out.</div>
          ) : p.lowStock ? (
            <div className="notice notice-error" style={{ maxWidth: 360, borderColor: '#e0cba8', background: '#fbf6ec', color: '#8a6a2a' }}>Low stock — only a few left.</div>
          ) : null}

          <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', border: '1px solid var(--ink)' }}>
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity" style={{ width: 38, border: 'none', background: 'none', cursor: 'pointer', fontSize: 15 }}>
                −
              </button>
              <span style={{ width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }} aria-live="polite">
                {qty}
              </span>
              <button onClick={() => setQty((q) => Math.min(20, q + 1))} aria-label="Increase quantity" style={{ width: 38, border: 'none', background: 'none', cursor: 'pointer', fontSize: 15 }}>
                +
              </button>
            </div>
            <button
              className="btn btn-solid"
              disabled={!p.inStock}
              style={{ flex: '1 1 200px', padding: '14px 30px' }}
              onClick={() => add(p.slug, qty, p.name)}
            >
              {p.inStock ? 'Add to bag' : 'Sold out'}
            </button>
          </div>

          {p.concerns.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {p.concerns.map((c) => (
                <Link key={c} to={`/shop?concern=${encodeURIComponent(c)}`} className="chip" style={{ textDecoration: 'none' }}>
                  {c}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
          {([['description', 'Description'], ['ingredients', 'Ingredients'], ['how', 'How to use']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                border: 'none', background: 'none', cursor: 'pointer', padding: '12px 20px',
                fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase',
                color: tab === key ? 'var(--ink)' : 'var(--muted)',
                borderBottom: `1px solid ${tab === key ? 'var(--rose)' : 'transparent'}`, marginBottom: -1,
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <p style={{ margin: '24px 0 0', fontSize: 13.5, fontWeight: 300, lineHeight: 1.9, color: '#463d36', maxWidth: '70ch' }}>
          {tab === 'description' ? p.description : tab === 'ingredients' ? p.ingredients : p.howToUse}
        </p>
      </section>

      <section className="section" style={{ background: 'var(--oat)' }}>
        <h2 className="rule-head" style={{ marginBottom: 28 }}>
          Reviews {p.reviewCount > 0 && `(${p.reviewCount})`}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 'clamp(20px,3vw,44px)', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {p.reviews.length === 0 ? (
              <p className="small" style={{ margin: 0 }}>
                No reviews yet. Be the first to write one.
              </p>
            ) : (
              p.reviews.map((r, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 18, borderBottom: '1px solid #ddd5cd' }}>
                  <div style={{ color: 'var(--rose-soft)', fontSize: 12, letterSpacing: '.2em' }}>{'★'.repeat(r.rating)}</div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 300, lineHeight: 1.8, color: '#4b423b' }}>“{r.body}”</p>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                    — {r.author}
                    {r.location ? `, ${r.location}` : ''}
                  </div>
                </div>
              ))
            )}
          </div>
          <ReviewForm slug={p.slug} onDone={reload} />
        </div>
      </section>

      {p.related.length > 0 && (
        <section className="section">
          <h2 className="rule-head">You may also like</h2>
          <div className="grid-products">
            {p.related.map((r) => (
              <ProductCard key={r.slug} product={r} onAdd={() => add(r.slug, 1, r.name)} />
            ))}
          </div>
        </section>
      )}
    </Shell>
  )
}
